import { useState } from "react";
import { motion } from "framer-motion";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from "../components/ui/select";
import { toast } from "sonner";
import { Send, CheckCircle } from "lucide-react";
import { getAttribution, pushEvent } from "../lib/tracking";

const WEB3FORMS_ACCESS_KEY = "8154fc60-d88b-478d-81d6-90386f980a48";
const WHATSAPP_PHONE = "5551997410236";
const WHATSAPP_LINK =
  "https://wa.me/5551997410236?text=Ol%C3%A1,%20estava%20no%20site%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es%20sobre%20o%20atendimento%20do%20*Dr%20Yago%20Almeida*.";

const PROCEDURES = [
  { group: "Corpo", items: [
    "Lipoaspiração de Definição (HD)",
    "Lipoaspiração de Braços e Pernas",
    "Tratamento de Lipedema",
    "Abdominoplastia",
    "MILA e MIRELA",
    "Mini-abdominoplastia",
    "Abdominoplastia Reversa",
    "Correção de diástase abdominal",
    "Correção de hérnias abdominais",
    "Lipoaspiração masculina",
    "Flancoplastia",
    "Gluteoplastia",
    "Lipoescultura",
    "Lipoenxertia",
    "Ninfoplastia (Cirurgia Íntima)",
    "Correção de cicatrizes",
    "Braquioplastia (Cirurgia dos Braços)",
    "Cruroplastia (Cirurgia das Coxas)",
    "Bodylift 360°",
    "Cirurgias Pós-bariátrica / Grande Emagrecimento",
  ]},
  { group: "Mama", items: [
    "Mamoplastia de Aumento (Recuperação Rápida) com prótese de silicone",
    "Mastopexia",
    "Mamoplastia Redutora",
    "Tratamento de Ginecomastia",
  ]},
  { group: "Face", items: [
    "Lifting Facial",
    "Cervicoplastia / Papada",
    "Bichectomia",
    "Blefaroplastia",
    "Liplift",
    "Browlift e Castanhares",
    "Nanofat",
    "Cantopexia",
  ]},
  { group: "Tecnologias", items: [
    "Renuvion",
    "Argoplasma e Argon4",
    "Morpheus",
    "Laser",
    "Bodytite",
    "Retraction",
    "Facetite",
    "Ignite RF",
    "Vibrolipoaspirador",
    "Lipoaspiração ultrassônica",
  ]},
  { group: "Cosmiatria", items: [
    "Toxina botulínica",
    "Preenchimentos e harmonização",
    "Bioestimuladores",
    "Skinbooster",
    "Fios de PDO",
    "Peeling",
    "Medicina Regenerativa",
    "Microagulhamento",
    "PDRN",
    "Exossomos",
    "PRP",
  ]},
  { group: "Outros", items: [
    "Outro / Não sei ainda",
  ]},
];

export const LeadForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    procedure: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, "");
    if (digits.length <= 2) return digits;
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    if (digits.length <= 11)
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setFormData((prev) => ({ ...prev, phone: formatted }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.procedure) {
      toast.error("Por favor, preencha todos os campos.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        access_key: WEB3FORMS_ACCESS_KEY,
        subject: `Novo Lead — ${formData.name} (${formData.procedure})`,
        from_name: "Site Dr. Yago Almeida",
        nome: formData.name,
        telefone: formData.phone,
        procedimento: formData.procedure,
        ...getAttribution(),
      };

      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || "Falha no envio");

      pushEvent("lead_form_submit", { form_location: "contato" });

      if (window.fbq) {
        window.fbq("track", "Lead", { content_name: formData.procedure });
      }

      const waText = encodeURIComponent(
        `Olá, sou *${formData.name}* e gostaria de mais informações sobre *${formData.procedure}* com o *Dr. Yago Almeida*. Meu telefone: ${formData.phone}.`
      );
      window.open(`https://wa.me/${WHATSAPP_PHONE}?text=${waText}`, "_blank", "noopener");

      setSubmitted(true);
      toast.success("Mensagem enviada com sucesso!");
    } catch {
      toast.error("Erro ao enviar. Tente novamente ou use o WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <section id="contato" data-testid="lead-form-section" className="py-28 md:py-40 bg-navy">
        <div className="max-w-2xl mx-auto px-5 md:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <CheckCircle className="text-gold/80 mx-auto mb-6" size={48} />
            <h2 className="font-heading text-white text-4xl md:text-5xl font-medium mb-4" data-testid="form-success-title">
              Mensagem Enviada
            </h2>
            <p className="font-body text-white/50 text-[15px] leading-relaxed mb-10">
              Nossa equipe entrará em contato para orientar os próximos passos.
            </p>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-white/15 text-white/70 font-body font-medium text-xs tracking-[0.1em] uppercase px-8 py-3.5 hover:bg-white/5 transition-all duration-300"
              data-testid="form-whatsapp-link"
              data-wa-location="formulario_sucesso"
            >
              Ou fale pelo WhatsApp
            </a>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="contato" data-testid="lead-form-section" className="py-28 md:py-40 bg-navy">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left - Text */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <p className="font-body text-gold/80 text-xs font-medium tracking-[0.35em] uppercase mb-4">
              Contato
            </p>
            <h2 className="font-heading text-white text-4xl md:text-5xl font-medium leading-tight mb-6">
              Converse com nossa equipe
            </h2>
            <div className="w-14 h-[1px] bg-gold/50 mb-8" />
            <p className="font-body text-white/65 text-[15px] leading-[1.8] mb-6">
              Preencha o formulário abaixo e nossa equipe entrará em contato para orientar os próximos passos e agendar sua consulta com segurança e praticidade.
            </p>
            <p className="font-body text-white/45 text-sm leading-relaxed">
              Caso prefira, utilize o WhatsApp para um atendimento mais direto.
            </p>
          </motion.div>

          {/* Right - Form */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="lead-form">
              <div>
                <Label className="font-body text-white/75 text-xs font-medium tracking-wide mb-2 block">
                  Nome completo
                </Label>
                <Input
                  type="text"
                  placeholder="Seu nome"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  className="form-input-custom bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/20 h-12 font-body text-sm focus-visible:ring-gold/50"
                  data-testid="lead-form-name"
                />
              </div>

              <div>
                <Label className="font-body text-white/75 text-xs font-medium tracking-wide mb-2 block">
                  Telefone
                </Label>
                <Input
                  type="tel"
                  placeholder="(00) 00000-0000"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  className="form-input-custom bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/20 h-12 font-body text-sm focus-visible:ring-gold/50"
                  data-testid="lead-form-phone"
                />
              </div>

              <div>
                <Label className="font-body text-white/75 text-xs font-medium tracking-wide mb-2 block">
                  Procedimento de interesse
                </Label>
                <Select
                  value={formData.procedure}
                  onValueChange={(val) => setFormData((prev) => ({ ...prev, procedure: val }))}
                >
                  <SelectTrigger
                    className="form-input-custom bg-white/[0.04] border-white/[0.08] text-white h-12 font-body text-sm focus:ring-gold/50 data-[placeholder]:text-white/20"
                    data-testid="lead-form-procedure"
                  >
                    <SelectValue placeholder="Selecione um procedimento" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-navy/10 max-h-60">
                    {PROCEDURES.map((group) => (
                      <SelectGroup key={group.group}>
                        <SelectLabel className="font-body text-navy font-semibold text-[10px] tracking-[0.1em] uppercase">
                          {group.group}
                        </SelectLabel>
                        {group.items.map((item) => (
                          <SelectItem
                            key={item}
                            value={item}
                            className="font-body text-navy/80 text-sm"
                          >
                            {item}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gold text-white font-body font-semibold text-xs tracking-[0.15em] uppercase px-8 py-4 hover:bg-gold/85 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 mt-2"
                data-testid="lead-form-submit"
              >
                {loading ? (
                  "Enviando..."
                ) : (
                  <>
                    Enviar Mensagem <Send size={14} />
                  </>
                )}
              </button>
              <p className="font-body text-white/40 text-[11px] text-center leading-relaxed">
                Ao enviar, você concorda com a nossa{" "}
                <a href="/privacidade" className="underline hover:text-gold transition-colors duration-300" data-testid="form-privacy-link">Política de Privacidade</a>.
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
